import * as React from "react";

// jaza-soft
import { PageHeader } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import CardHeader from "../../../../components/CardHeader";

import useQuery from "../../../../hooks/useQuery";
import { isEmpty, isTrue, toFixed, toFixedValue } from "../../../../utils/helpers";
import { formatDateTime } from "../../../../utils/dateUtils";
import useSafeState from "../../../../hooks/useSafeState";
import { useDeepMemo, useEntityPrivilege, useSetting, useTime } from "../../../../hooks";
import { Entity, InventoryStatus, QcStatus } from "../../../../utils/types";
import { colorLightGreen, colorOrange, colorSecondary, colorTomatoRed } from "../../../../utils/colors";
import SettingKeys from "../../../../utils/SettingKeys";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";
import useWarehouse from "../../../../hooks/useWarehouse";
import { UOM } from "../../../../constants";

import ItemTransferDrawer from "./ItemTransferDrawer";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
          ? colorTomatoRed.dark
          : value === QcStatus.ON_HOLD
          ? colorOrange.dark
          : value === QcStatus.PENDING
          ? colorSecondary.dark
          : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const MaterialTransferItemView = props => {
  const { roles, hasAccess, history } = props;
  const fabricColorId = props.match.params.id;

  const [version, setVersion] = useSafeState(0);

  const [filterValues, setFilterValues] = useSafeState({});
  const [drawerState, setDrawerState] = useSafeState({ open: false, rolls: [], ledger: null });

  const { disableCreate } = useEntityPrivilege("MaterialTransfer");
  const { utc_offset } = useTime();
  const { allWhList } = useWarehouse();

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);

  const { data: fabricColor = {} } = useQuery(fabricColorId && { url: `fabric-colors/${fabricColorId}`, version });
  const { data: fabItemList = [] } = useQuery(
    !isEmpty(fabricColor) && {
      url: `fabric-items`,
      params: { search: `fabric.id==${fabricColor.fabric?.id};color=='${fabricColor.color}'`, size: 10000 }
    }
  );
  const fabricItemList = fabItemList?.filter(e => e.effFabColor === fabricColor.effColor) || [];
  const fabricItemIds = fabricItemList.map(e => e.id);

  const { data: rollList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: "rolls",
      params: {
        search: `fabricItem.id=in=(${fabricItemIds.join(",")});parentId=='NULL';active=in=('TRUE', 'FALSE')`,
        size: 10000
      },
      version
    }
  );
  const { data: itemLedgerList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: "ledgers",
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});type=='ITEM_TRANSFER'`, size: 10000 },
      version
    }
  );

  const infoColumns = useDeepMemo(() => {
    let columns = [
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "effColor", title: "Fabric Color" },
      { dataKey: "articleName", title: "article Name", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "itemDesc", title: "Item Description", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "classification", title: "Classification", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "fabricCategoryName", title: "Fabric Category", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "fabricPatternName", title: "Fabric Pattern", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "construction", title: "Count Contruction", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "content", title: "Content", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "repeat", title: "Repeat", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "gsm", title: "GSM", render: value => (isEmpty(value) ? "-" : value) }
    ];
    return columns;
  }, []);

  const rollColumns = useDeepMemo(() => {
    let columnList = [
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "rollNo", title: "Roll No" },
      { dataKey: "invStatus", title: "Inv. Status", align: "center", filterOptions: { filter: true } },
      { dataKey: "qcStatus", title: "QC Status", align: "center", filterOptions: { filter: true }, render: renderQcStatus },
      { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 2)} m`) },
      { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`) },
      { dataKey: "invoiceNo", title: "Invoice No", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      { dataKey: "colorShade", title: "Color Shade", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fpo", title: "Fabric PO", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`),
        filterOptions: { filter: true }
      },
      { dataKey: "grnWarehouse", filterOptions: { filter: true }, title: "GRN \nWarehouse", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "currWarehouse", filterOptions: { filter: true }, title: "Current \nWarehouse", render: (value, shortValue) => shortValue || "-" }
    ];
    if (rollList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      columnList = columnList.filter(c => c.dataKey !== "weight");
    }
    return columnList;
  }, [rollList]);

  const infoRows = useDeepMemo(() => {
    if (isEmpty(fabricColor)) return [];
    const fabric = fabricColor.fabric || {};
    const infoRows = [
      {
        ...fabric,
        ...fabricColor,
        fabricCategoryName: fabric.fabricCategory?.name,
        fabricPatternName: fabric.fabricPattern?.name,
        repeat:
          !isEmpty(fabric.repeatLength) && !isEmpty(fabric.repeatWidth)
            ? `${fabric.repeatLength} X ${fabric.repeatWidth}`
            : !isEmpty(fabric.repeatLength)
            ? fabric.repeatLength
            : !isEmpty(fabric.repeatWidth)
            ? fabric.repeatWidth
            : ""
      }
    ];
    return infoRows;
  }, [fabricColor, utc_offset, allWhList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList) || isEmpty(fabricItemList) || isEmpty(fabricColor)) return [];
    const rowList = rollList
      .filter(ri => isTrue(ri.active))
      .filter(
        ri =>
          ri.invStatus === InventoryStatus.UNALLOCATED ||
          (ri.invStatus === InventoryStatus.DISABLED && ri.prevInvStatus === InventoryStatus.UNALLOCATED)
      )
      .filter(ri => fabricItemIds.includes(ri.fabricItemId))
      .map(rollInfo => {
        const fabricItem = fabricItemList?.find(e => e.id === rollInfo.fabricItemId) || {};
        return {
          ...rollInfo,
          ...fabricItem,
          parentId: undefined,
          fabricItem,
          id: rollInfo.id,
          itemCode: fabricItem?.fabric?.itemCode,
          invoiceNo: fabricItem.invoice?.invoiceNo,
          grnWarehouse: allWhList?.find(w => w.code === fabricItem.warehouse)?.name || fabricItem.warehouse,
          currWarehouse: allWhList?.find(w => w.code === rollInfo.currWarehouse)?.name || rollInfo.currWarehouse,
          grnWarehouseCode: fabricItem.warehouse,
          currWarehouseCode: rollInfo.currWarehouse
        };
      });
    return rowList;
  }, [rollList, fabricItemIds, fabricItemList, allWhList]);

  const itemTxnColumns = useDeepMemo(() => {
    // let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields, "associatedQty", "allocatedQty"];
    // if (allWhList?.length > 1) {
    //   fields.push("warehouse");
    // }
    const columns = [
      { dataKey: "txn", title: "TXN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "toItemCode", title: "To Item Code" },
      { dataKey: "toEffFabColor", title: "To Item Color" },
      {
        dataKey: "invoiceNo",
        title: "Invoice No",
        render: (value, shortValue) => shortValue || "-",
        linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`),
        filterOptions: { filter: true }
      },
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      { dataKey: "colorShade", title: "Color Shade", filterOptions: { filter: true } },
      { dataKey: "fabricPo", title: "Fabric PO" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`),
        filterOptions: { filter: true }
      },
      {
        dataKey: "transferredQty",
        title: "Transferred Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? ""
            : `${toFixed(value)} m${row.uomLength !== UOM.METER.value ? ` (${toFixedValue(row.ledgerQty)} ${UomCodeFromValue[row.uomLength]})` : ""}`
      },
      { dataKey: "time", title: "Time" },
      { dataKey: "user", title: "User", filterOptions: { filter: true } }
    ];
    return columns;
  }, [invoiceFields, fabricFields, fabricItemFields, allWhList]);

  const itemTxnRows = useDeepMemo(() => {
    if (isEmpty(itemLedgerList)) return [];
    return itemLedgerList
      .sort((a, b) => b.id - a.id)
      .map(ledger => {
        return {
          ...ledger,
          toEffFabColor: `${ledger.toItemColor}${!isEmpty(ledger.toItemColorCode) ? `(${ledger.toItemColorCode})` : ""}`,
          invoiceNo: ledger.invoice?.invoiceNo,
          grn: ledger.fabricItem?.grn,
          colorShade: ledger.fabricItem?.colorShade,
          fabricPo: ledger.fabricItem?.fabricPo,
          orderingWidth: ledger.fabricItem?.orderingWidth,
          uomWidth: ledger.fabricItem?.uomWidth,
          uomLength: ledger.fabricItem?.uomLength,
          transferredQty: ledger.qty,
          time: !isEmpty(utc_offset) ? formatDateTime(ledger.createdAt, utc_offset) : "-",
          user: ledger.createdBy
        };
      });
  }, [itemLedgerList, allWhList, utc_offset]);

  const selectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "transfer",
        tooltip: "Transfer",
        showLabel: true,
        options: { variant: "contained", color: "primary", style: { marginLeft: "1em", width: 120 } }
      }
    ];
    return actions;
  }, []);

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (action === "transfer") {
      setDrawerState({ open: true, rollList: selectedRows });
    }
  };

  const filteredQty = rows
    .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
    .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
    .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
    .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus)
    .reduce((acc, e) => acc + e.length, 0);

  return (
    <div>
      {/** Header Element */}
      <PageHeader title="Material Transfer - By Item" />

      <ItemTransferDrawer
        open={drawerState?.open || false}
        ledger={drawerState?.ledger}
        fabricColor={fabricColor}
        rollList={drawerState?.rollList}
        roles={roles}
        hasAccess={hasAccess}
        onRefresh={() => setVersion(v => v + 1)}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <div style={{ margin: "1.5em", marginTop: 0 }}>
        {/* PO Details */}
        <MuiTable title="Item Details" toolbar columns={infoColumns} rows={infoRows} i18nKey="resources.fabricItems.fields" />

        {/* Warehouse Transfer Transactions */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Transfer History" />}
            columns={itemTxnColumns}
            rows={itemTxnRows}
            onRowClick={row =>
              setDrawerState({
                open: true,
                rollList: Array.isArray(rollList) ? rollList.filter(ri => row.rollList?.map(e => e.id).includes(ri.id)) : row.rollList,
                ledger: row
              })
            }
            searchable
            pageable
            searchKeys={["txn", "fabricPo"]}
            i18nKey="resources.quality.fields.invoice"
            emptyMessage="Transaction history not available"
          />
        </div>

        {/** Roll Inventory */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            key={version}
            title={`Roll Inventory (${Number(toFixed(filteredQty, 1))})`}
            titleSelected={selectedRows =>
              `${selectedRows?.length} Rolls (${Number(toFixed(selectedRows.reduce((acc, e) => acc + e.length, 0)))}) selected`
            }
            columns={rollColumns}
            rows={rows}
            pageable
            searchable
            selectable={hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_TRANSFER, Scope.WRITE) && !disableCreate}
            searchKeys={["rollNo"]}
            selectActions={selectActions}
            onSelectActionClick={onSelectActionClick}
            onFilter={setFilterValues}
            fontSize={16}
            pageSize={15}
            rowsPerPageOptions={[15, 25, 100]}
            i18nKey="resources.rolls.fields"
            emptyMessage="Unallocated Stock not available"
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialTransferItemView;
